import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppSearch } from "./AppSearch";
import { WhatsAppAddress } from "../../Shared/WhatsAppAddress";

// AppForward - "Forward" from a bubble to another 1:1 contact (2026-10-14).
//
// ⛔ DECISION 9: the bubble's Forward opens a PICKER and sends nothing; the one click on a contact's "Forward" button is
// the approval, and it goes through `AppComposer.SendAction` (the ONE latch) -> `AppMain.QueueAction` (the page-wide
// ticket). TEXT goes as a schema-4 `forward` the service resolves from the subscriber's own records; a PHOTO / VOICE NOTE
// the page holds goes through the existing attachment path (schema 3) - the page reads the bubble's own blob.

export class AppForwardTarget {
    public Href: string = "";
    public Title: string = "";
    public ChatId: string = "";
}

export class AppForwardRules {
    public static readonly MaxShown: number = 50;
    public static readonly DialogId: string = "wac-forward";
    public static readonly PickAttr: string = "data-wac-fwd";

    /** The owner's own 1:1 contacts other than `ExceptChat`, filtered by folded words (all must match), newest first kept. */
    public static Filter = (All: AppForwardTarget[], ExceptChat: string, Query: string): AppForwardTarget[] => {
        const Out: AppForwardTarget[] = [];
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            const Words: string[] = AppSearch.Fold(nz(Query)).split(" ").filter(AppForwardRules.NotEmpty);
            for (const One of All) {
                if (Out.length >= AppForwardRules.MaxShown) { break; }
                if (One.ChatId === ExceptChat || WhatsAppAddress.IsJid(One.ChatId) === false || WhatsAppAddress.IsGroup(One.ChatId) === true) { continue; }
                const Hay: string = AppSearch.Fold(One.Title + " " + One.ChatId);
                if (AppForwardRules.AllIn(Hay, Words) === true) { Out.push(One); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppForwardRules.Filter");
            return [];
        }
    };

    /** DOCUMENTED EXCEPTION: a named predicate for `filter`. */
    public static NotEmpty = (W: string): boolean => { return W.length > 0; };

    /** DOCUMENTED EXCEPTION: pure - every word in the haystack. */
    public static AllIn = (Hay: string, Words: string[]): boolean => {
        for (const W of Words) { if (Hay.indexOf(W) < 0) { return false; } }
        return true;
    };
}

interface IFwNode {
    textContent: string | null;
    hidden: boolean;
    className: string;
    value?: string;
    open?: boolean;
    setAttribute(N: string, V: string): void;
    getAttribute(N: string): string | null;
    appendChild(C: IFwNode): IFwNode;
    addEventListener(T: string, F: (Ev: Event) => void): void;
    remove?(): void;
    showModal?(): void;
    close?(): void;
    focus?(): void;
}

export interface IFwDoc {
    createElement(Tag: string): IFwNode;
    body: IFwNode | null;
}

/** What the picker's click does - `AppMain.ForwardTo`. ⛔ The one click; nothing else here reaches a sender. */
export interface IFwHost {
    Picked(Target: AppForwardTarget): void;
}

/**
 * The picker: a native modal `<dialog>` ("Forward to…", a search box, one button per contact, Cancel). Built per open,
 * removed on close; the list is rewritten only on typing (no control under the pointer is rebuilt by a hover).
 */
export class AppForwardPicker {
    private Doc: IFwDoc;
    private Host: IFwHost;
    private All: AppForwardTarget[];
    private Except: string;
    private Dialog: IFwNode | null = null;
    private List: IFwNode | null = null;
    private Shown: AppForwardTarget[] = [];

    public constructor(Doc: IFwDoc, Host: IFwHost, All: AppForwardTarget[], ExceptChat: string) {
        this.Doc = Doc;
        this.Host = Host;
        this.All = All;
        this.Except = ExceptChat;
    }

    public Open = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc.body === null) { return; }
            const D: IFwNode = this.Doc.createElement("dialog");
            D.className = "wac-forward";
            D.setAttribute("id", AppForwardRules.DialogId);
            D.setAttribute("aria-labelledby", "wac-fwdtitle");
            const Title: IFwNode = this.Doc.createElement("h2");
            Title.setAttribute("id", "wac-fwdtitle");
            Title.textContent = "Forward to…";
            const Find: IFwNode = this.Doc.createElement("input");
            Find.setAttribute("type", "search");
            Find.setAttribute("aria-label", "Find a contact");
            Find.addEventListener("input", this.OnInput);
            const List: IFwNode = this.Doc.createElement("ul");
            List.className = "wac-fwdlist";
            const Cancel: IFwNode = this.Doc.createElement("button");
            Cancel.setAttribute("type", "button");
            Cancel.className = "btn secondary";
            Cancel.textContent = "Cancel";
            Cancel.addEventListener("click", this.Close);
            D.appendChild(Title); D.appendChild(Find); D.appendChild(List); D.appendChild(Cancel);
            D.addEventListener("click", this.OnClick);
            D.addEventListener("close", this.OnClosed);
            (this.Doc.body as IFwNode).appendChild(D);
            this.Dialog = D;
            this.List = List;
            this.Fill("");
            if (typeof D.showModal === "function") { (D.showModal as () => void)(); }
            if (typeof Find.focus === "function") { (Find.focus as () => void)(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppForwardPicker.Open");
        }
    };

    /** The contacts for a query: one row per contact, its button labelled "Forward to <name>". */
    public Fill = (Query: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.List === null) { return; }
            const List: IFwNode = this.List as IFwNode;
            List.textContent = "";
            this.Shown = AppForwardRules.Filter(this.All, this.Except, Query);
            for (let At: number = 0; At < this.Shown.length; At = At + 1) {
                const Li: IFwNode = this.Doc.createElement("li");
                const B: IFwNode = this.Doc.createElement("button");
                B.setAttribute("type", "button");
                B.className = "btn ghost wac-fwdpick";
                B.setAttribute(AppForwardRules.PickAttr, String(At));
                B.setAttribute("aria-label", "Forward to " + this.Shown[At].Title);
                B.textContent = this.Shown[At].Title;
                Li.appendChild(B);
                List.appendChild(Li);
            }
            if (this.Shown.length === 0) {
                const Li: IFwNode = this.Doc.createElement("li");
                Li.className = "wac-fwdnone";
                Li.textContent = "No contact matches.";
                List.appendChild(Li);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppForwardPicker.Fill");
        }
    };

    private OnInput = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Fill(nz((Ev.target as unknown as IFwNode).value)); } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppForwardPicker.OnInput"); }
    };

    /** ⛔ THE ONE CLICK: a contact's button. The dialog closes first, then the host is told once. */
    public OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const T: { closest?: (S: string) => IFwNode | null } = Ev.target as unknown as { closest?: (S: string) => IFwNode | null };
            if (T === null || typeof T.closest !== "function") { return; }
            const B: IFwNode | null = (T.closest as (S: string) => IFwNode | null)("[" + AppForwardRules.PickAttr + "]");
            if (B === null) { return; }
            const At: number = Number(B.getAttribute(AppForwardRules.PickAttr));
            const Target: AppForwardTarget | undefined = this.Shown[At];
            this.Close();
            if (Target !== undefined) { this.Host.Picked(Target); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppForwardPicker.OnClick");
        }
    };

    public Close = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Dialog === null) { return; }
            const D: IFwNode = this.Dialog as IFwNode;
            this.Dialog = null;
            if (typeof D.close === "function" && D.open === true) { (D.close as () => void)(); }
            if (typeof D.remove === "function") { (D.remove as () => void)(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppForwardPicker.Close");
        }
    };

    /** Esc closed it: removed, nothing sent. */
    private OnClosed = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Close(); } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppForwardPicker.OnClosed"); }
    };
}
